import { createSlice } from '@reduxjs/toolkit';
import { getDefaultParticleShape } from '../../utils/particles/particleShapeTextures';

// Enhanced particle schema with all dynamic properties
const particleTemplate = () => ({
  id: '',
  name: '',
  type: '', // fire, rain, snow, stars, clouds, terrain, water, foliage
  parent: null,
  range: 1, // emitter radius or area
  intensity: 50, // emission rate
  color: '#FFFFFF', // start color
  position: { x: 0, y: 0, z: 0 },
  rotation: { x: 0, y: 0, z: 0 },
  scale: { x: 1, y: 1, z: 1 },

  // Animation state
  isPlaying: true, // true = playing, false = paused
  isStopped: false, // true = stopped, false = running

  // Particle count properties
  particleCount: 1000,

  // Material properties
  size: 1.0,
  opacity: 0.9,
  sizeAttenuation: true,
  blending: 'AdditiveBlending', // AdditiveBlending, NormalBlending, etc.

  // Shape properties
  shape: 'circle', // circle, square, triangle, star, diamond, cross, hexagon, snow (+ fire, rain procedural)
  shapeRotation: 0, // rotation in radians
  shapeScale: 1.0, // scale factor for the shape

  // Physics properties
  velocity: { x: 0, y: 0, z: 0 },
  acceleration: { x: 0, y: 0, z: 0 },
  turbulence: 0.1,
  wind: { x: 0, y: 0, z: 0 },

  // Life cycle properties
  life: 120.0,
  lifeVariation: 0.2,

  // Color properties
  colorVariation: 0.1,
  colorTransition: true,

  // Size properties
  sizeVariation: 0.3,
  sizeTransition: true,

  // Type-specific properties (additional fields)
  // Fire specific
  coreCount: 100,
  emberCount: 150,
  heatIntensity: 0.8,

  // Rain specific
  dropSize: 0.1,
  splashEffect: true,

  // Snow specific
  flakeSize: 0.05,
  windEffect: 0.5,

  // Stars specific
  twinkleSpeed: 1.0,
  twinkleIntensity: 0.5,

  // Clouds specific
  clusterCount: 15,
  particlesPerCluster: { min: 15, max: 25 },
  cloudDensity: 0.7,

  // Terrain specific
  terrainHeight: 100,
  terrainWidth: 200,
  terrainSegments: 32,

  // Water specific
  waveHeight: 0.8,
  waveSpeed: 0.01,
  waveFrequency: 0.015,

  // Foliage specific
  treeCount: 10,
  bushCount: 10,
  grassCount: 50,
  pineColor: '#1a4d2e',
  oakColor: '#228b22',
  birchColor: '#9acd32',
  grassColor: '#228b22',
  bushColor: '#228b22',
  forestRadius: 3,
  forestSpread: 40,
  leafCount: 100,
  swayIntensity: 0.3,
  swaySpeed: 1.0,

  // Editor visibility (persisted in particle_properties on publish)
  visible: true,
});

const initialState = {
  particles: [], // Array of enhanced particle system objects
  selectedParticle: null,
  lastUpdatedParticleId: null, // Track which particle was last updated
};

/** Strip trailing " 1", " 2", etc. so duplicates always share one family name. */
function resolveParticleBaseName(name, type) {
  const typeName = type
    ? type.charAt(0).toUpperCase() + type.slice(1)
    : 'Particle';
  const rawName = name && String(name).trim() !== '' ? String(name).trim() : typeName;
  const withoutSuffix = rawName.replace(/\s+\d+$/, '').trim();
  return withoutSuffix || typeName;
}

function getNextDuplicateParticleName(particles, baseName) {
  let maxNum = 0;
  particles.forEach((p) => {
    if (!p.name) return;
    if (p.name === baseName) {
      maxNum = Math.max(maxNum, 0);
      return;
    }
    const match = p.name.match(
      new RegExp(`^${baseName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s+(\\d+)$`)
    );
    if (match) {
      maxNum = Math.max(maxNum, parseInt(match[1], 10));
    }
  });
  return `${baseName} ${maxNum + 1}`;
}

const particleSlice = createSlice({
  name: 'particles',
  initialState,
  reducers: {
    // addParticle: (state, action) => {
    //   // If payload is partial, fill with template
    //   const payload = {
    //     ...particleTemplate(),
    //     ...action.payload,
    //     temp_file_name: action.payload.id,
    //   };
    //   // If particle is a cloud and no range was provided, use default range 8
    //   if ((payload.type === 'cloud' || payload.type === 'clouds') && (action.payload == null || action.payload.range === undefined)) {
    //     payload.range = 8;
    //   }
    //    // If particle is a cloud and no range was provided, use default range 8
    //   if ((payload.type === 'foliage' ) && (action.payload == null || action.payload.range === undefined)) {
    //     payload.range = 10;
    //   }
    //   state.particles.push(payload);
    // },

    addParticle: (state, action) => {
      const payload = {
        ...particleTemplate(),
        ...action.payload,
        temp_file_name: action.payload.id,
      };

      if (action.payload.shape == null || action.payload.shape === '') {
        payload.shape = getDefaultParticleShape(payload.type);
      }

      if (
        (payload.type === 'cloud' || payload.type === 'clouds') &&
        (action.payload == null || action.payload.range === undefined)
      ) {
        payload.range = 20;
      }
      if (
        payload.type === 'foliage' &&
        (action.payload == null || action.payload.range === undefined)
      ) {
        payload.range = 10;
      }
        if (
        payload.type === 'snow' &&
        (action.payload == null || action.payload.range === undefined)
      ) {
        payload.range = 50;
      }
         if (
        payload.type === 'rain' &&
        (action.payload == null || action.payload.range === undefined)
      ) {
        payload.range = 50;
      }

      // Naming: preserve saved/API names (publish reload, undo). Only auto-name
      // brand-new creates and explicit duplicates.
      if (action.payload?.name && !action.payload.isDuplicate) {
        payload.name = action.payload.name;
      } else {
        const baseName = resolveParticleBaseName(payload.name, payload.type);

        const sameBaseParticles = state.particles.filter((p) => {
          if (!p.name) return false;
          if (p.name === baseName) return true;
          return p.name.match(
            new RegExp(
              `^${baseName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s+\\d+$`
            )
          );
        });

        if (!payload.isDuplicate || sameBaseParticles.length === 0) {
          payload.name = baseName;
        } else {
          payload.name = getNextDuplicateParticleName(
            sameBaseParticles,
            baseName
          );
        }
      }

      state.particles.push(payload);
      state.lastUpdatedParticleId = payload.id;
    },
    updateParticle: (state, action) => {
      const { id, updates } = action.payload;
      const idx = state.particles.findIndex((p) => p.id === id);
      if (idx !== -1) {
        state.particles[idx] = { ...state.particles[idx], ...updates };
        state.lastUpdatedParticleId = id; // Track which particle was updated
      }
    },
    removeParticle: (state, action) => {
      state.particles = state.particles.filter((p) => p.id !== action.payload);
    },
    clearParticles: (state) => {
      state.particles = [];
    },
    setSelectedParticle: (state, action) => {
      state.selectedParticle = action.payload;
    },
    clearLastUpdatedParticle: (state) => {
      state.lastUpdatedParticleId = null;
    },
    // Animation control actions
    playParticle: (state, action) => {
      const idx = state.particles.findIndex((p) => p.id === action.payload);
      if (idx !== -1) {
        state.particles[idx].isPlaying = true;
        state.particles[idx].isStopped = false;
        state.lastUpdatedParticleId = action.payload;
      }
    },
    pauseParticle: (state, action) => {
      const idx = state.particles.findIndex((p) => p.id === action.payload);
      if (idx !== -1) {
        state.particles[idx].isPlaying = false;
        state.particles[idx].isStopped = false;
        state.lastUpdatedParticleId = action.payload;
      }
    },
    stopParticle: (state, action) => {
      const idx = state.particles.findIndex((p) => p.id === action.payload);
      if (idx !== -1) {
        state.particles[idx].isPlaying = false;
        state.particles[idx].isStopped = true;
        state.lastUpdatedParticleId = action.payload;
      }
    },
    setParticlesHistory: (state, action) => {
      state.particles = action.payload || [];
    },
  },
});

export const {
  addParticle,
  updateParticle,
  removeParticle,
  clearParticles,
  setSelectedParticle,
  clearLastUpdatedParticle,
  playParticle,
  pauseParticle,
  stopParticle,
  setParticlesHistory,
} = particleSlice.actions;

export default particleSlice.reducer;
